import { useCallback, useEffect, useState } from "react"
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Typography } from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { LoadingIcon } from "components/Common/LoadingIcon"
import { Title } from "components/Common/Title"
import analyticsService from "services/analyticsService"
import { hiabColors } from "theme"
import type { IAnalyticsLicenseOrg } from "types/analyticsTypes"

type ViewFilter = "bottom10" | "top10" | "all"

type ChartDataPoint = {
  orgId: string
  name: string
  utilization: number
  assigned: number
  total: number
}

function buildChartData(orgs: IAnalyticsLicenseOrg[]): ChartDataPoint[] {
  return orgs.map((org) => ({
    orgId: org.orgId,
    name: org.orgName,
    utilization: org.utilizationPercent,
    assigned: org.assignedLicenses,
    total: org.totalLicenses,
  }))
}

function applyFilter(orgs: IAnalyticsLicenseOrg[], filter: ViewFilter): IAnalyticsLicenseOrg[] {
  switch (filter) {
    case "top10":
      return [...orgs].sort((a, b) => b.utilizationPercent - a.utilizationPercent).slice(0, 10)
    case "bottom10":
      return [...orgs].sort((a, b) => a.utilizationPercent - b.utilizationPercent).slice(0, 10)
    case "all":
      return [...orgs].sort((a, b) => a.utilizationPercent - b.utilizationPercent) // lowest utilization first
  }
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ payload: ChartDataPoint }>
  label?: string
}) {
  if (!active || !payload || payload.length === 0) return null

  const { assigned, total, utilization } = payload[0].payload

  return (
    <Paper sx={{ p: 1.5 }}>
      <Typography variant="body2" fontWeight="bold">
        {label}
      </Typography>
      <Typography variant="body2">Assigned: {assigned}</Typography>
      <Typography variant="body2">Total: {total}</Typography>
      <Typography variant="body2">Utilization: {utilization}%</Typography>
    </Paper>
  )
}

export function LicenseHealthTab() {
  const [data, setData] = useState<IAnalyticsLicenseOrg[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ViewFilter>("bottom10")

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getLicenses()
      setData(result)
    } catch {
      setError("Failed to load license utilization data.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleFilterChange = (event: SelectChangeEvent<ViewFilter>) => {
    setFilter(event.target.value as ViewFilter)
  }

  if (loading) return <LoadingIcon />

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>LICENSE UTILIZATION BY ORGANIZATION</Title>
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    )
  }

  if (data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Title>LICENSE UTILIZATION BY ORGANIZATION</Title>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No license data available.
        </Typography>
      </Paper>
    )
  }

  const filtered = applyFilter(data, filter)
  const chartData = buildChartData(filtered)
  const chartHeight = filter === "all" ? Math.max(300, data.length * 40) : Math.max(300, 10 * 40)

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Title>LICENSE UTILIZATION BY ORGANIZATION</Title>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Show</InputLabel>
          <Select<ViewFilter> value={filter} label="Show" onChange={handleFilterChange}>
            <MenuItem value="bottom10">Bottom 10 (lowest utilization)</MenuItem>
            <MenuItem value="top10">Top 10 (highest utilization)</MenuItem>
            <MenuItem value="all">All ({data.length} orgs)</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Percentage of licenses assigned to users per organization. Red bars indicate utilization
        below 50%.
      </Typography>
      <Box sx={{ overflowY: filter === "all" ? "auto" : "visible", maxHeight: filter === "all" ? 500 : "none" }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ left: 120, right: 60, top: 8, bottom: 8 }}
          >
            <XAxis type="number" domain={[0, 100]} unit="%" />
            <YAxis type="category" dataKey="name" width={120} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="utilization">
              <LabelList dataKey="utilization" position="right" formatter={(v: number) => `${v}%`} />
              {chartData.map((entry) => (
                <Cell
                  key={entry.orgId}
                  fill={entry.utilization < 50 ? hiabColors.redPrimary : hiabColors.bluePrimary}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  )
}
